import { useState, useEffect } from "react";
import {
  RefreshCw, TrendingUp, TrendingDown, DollarSign, Eye, MousePointer,
  Target, CheckCircle, AlertTriangle, Unplug, BarChart2, LogIn,
  Filter, Clock, Zap
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const safeJson = async (res) => {
  try { return await res.json(); } catch (e) {
    if (e.message?.includes("body stream already read") || e.message?.includes("locked")) {
      try { return await res.clone().json(); } catch { return {}; }
    }
    return {};
  }
};

const PERIODS = [
  { value: 7, label: "7j" },
  { value: 14, label: "14j" },
  { value: 30, label: "30j" },
  { value: 90, label: "90j" },
];

const STATUS_FILTERS = [
  { value: "active", label: "Actives" },
  { value: "all", label: "Toutes" },
  { value: "paused", label: "En pause" },
];

export default function GoogleAdsSection() {
  const [authStatus, setAuthStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => { checkAuthStatus(); }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'google-ads-auth') {
        setConnecting(false);
        if (event.data.success) checkAuthStatus();
        else setError(event.data.message || "Erreur lors de la connexion");
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (authStatus.connected) loadGoogleAdsData();
  }, [authStatus.connected, days]);

  const checkAuthStatus = async () => {
    try {
      const res = await authFetch(`${API}/google-ads/auth/status`);
      const data = await safeJson(res);
      setAuthStatus({ ...data, loading: false });
    } catch {
      setAuthStatus({ connected: false, loading: false });
    }
  };

  const loadGoogleAdsData = async () => {
    setLoadingData(true);
    setError(null);
    try {
      const [campaignsRes, statsRes] = await Promise.all([
        authFetch(`${API}/google-ads/campaigns?days=${days}`),
        authFetch(`${API}/google-ads/stats?days=${days}`)
      ]);
      const campaignsData = await safeJson(campaignsRes);
      const statsData = await safeJson(statsRes);
      setCampaigns(campaignsData.campaigns || []);
      setStats(statsData);
    } catch {
      setError("Erreur lors du chargement des donnees Google Ads");
    }
    setLoadingData(false);
  };

  const startAuth = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/google-ads/auth/url`);
      const data = await safeJson(res);
      if (data.auth_url) {
        const w = 600, h = 700;
        const popup = window.open(data.auth_url, 'google-ads-auth', `width=${w},height=${h},scrollbars=yes`);
        if (!popup) { setError("Popup bloque."); setConnecting(false); return; }
        const chk = setInterval(() => {
          if (popup.closed) { clearInterval(chk); setConnecting(false); setTimeout(checkAuthStatus, 1000); }
        }, 1000);
      }
    } catch (e) { setError(e.message); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Deconnecter Google Ads ?")) return;
    await authFetch(`${API}/google-ads/auth/disconnect`, { method: "POST" });
    setAuthStatus({ connected: false, loading: false });
    setCampaigns([]);
    setStats(null);
  };

  const fmt = v => (v ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  const fmtN = v => (v ?? 0).toLocaleString('fr-FR');

  // Filter campaigns by status
  const filtered = campaigns.filter(c => {
    if (statusFilter === "active") return c.status === "ENABLED";
    if (statusFilter === "paused") return c.status !== "ENABLED";
    return true;
  });

  // Compute alerts
  const alerts = [];
  if (stats && campaigns.length > 0) {
    const activeCampaigns = campaigns.filter(c => c.status === "ENABLED");
    if (stats.ctr < 1.0) alerts.push({ type: "warning", msg: `CTR bas (${stats.ctr.toFixed(2)}%) — verifiez vos annonces` });
    if (stats.avg_cpc > 2.0) alerts.push({ type: "warning", msg: `CPC eleve (${fmt(stats.avg_cpc)}) — optimisez vos encheres` });
    if (stats.conversions === 0 && stats.clicks > 50) alerts.push({ type: "danger", msg: `${fmtN(stats.clicks)} clics sans conversion — verifiez le tracking` });
    activeCampaigns.forEach(c => {
      if (c.budget > 0 && c.cost < c.budget * 0.5 * (days >= 30 ? 30 : days)) {
        alerts.push({ type: "info", msg: `"${c.name.substring(0, 40)}" — budget sous-consomme` });
      }
    });
  }

  if (authStatus.loading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!authStatus.connected) {
    return (
      <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
            <BarChart2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Google Ads</h3>
            <p className="text-sm text-slate-400">Non connecte</p>
          </div>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        <button onClick={startAuth} disabled={connecting} data-testid="google-ads-connect-btn"
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all">
          {connecting ? <><RefreshCw className="w-5 h-5 animate-spin" /> Connexion...</> : <><LogIn className="w-5 h-5" /> Connecter Google Ads</>}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold">Google Ads</h3>
              <p className="text-sm text-green-400 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full" />
                Connecte — ID: {authStatus.customer_id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadGoogleAdsData} disabled={loadingData}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors" title="Actualiser"
              data-testid="refresh-campaigns-btn">
              <RefreshCw className={loadingData ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
            </button>
            <button onClick={disconnect} className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors" title="Deconnecter">
              <Unplug className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-1.5" data-testid="alerts-section">
          {alerts.slice(0, 3).map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              a.type === "danger" ? "bg-red-500/15 border border-red-500/25 text-red-400" :
              a.type === "warning" ? "bg-amber-500/15 border border-amber-500/25 text-amber-400" :
              "bg-blue-500/15 border border-blue-500/25 text-blue-400"
            }`}>
              {a.type === "danger" ? <Zap className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
              <span>{a.msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          <KPI icon={Eye} label="Impressions" value={fmtN(stats.impressions)} color="blue" />
          <KPI icon={MousePointer} label="Clics" value={fmtN(stats.clicks)} color="green" />
          <KPI icon={DollarSign} label="Cout total" value={fmt(stats.cost)} color="amber" />
          <KPI icon={Target} label="Conversions" value={fmtN(stats.conversions)} color="purple" />
          <KPI icon={TrendingUp} label="CTR" value={(stats.ctr || 0).toFixed(2) + "%"} color="cyan" alert={stats.ctr < 1.0} />
          <KPI icon={BarChart2} label="CPC moyen" value={fmt(stats.avg_cpc)} color="pink" alert={stats.avg_cpc > 2.0} />
          <KPI icon={Zap} label="Cout/Conv" value={stats.cost_per_conversion ? fmt(stats.cost_per_conversion) : "-"} color="emerald" highlight />
        </div>
      )}

      {/* Toolbar: Period selector + Status filter */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <BarChart2 className="w-4 h-4 text-blue-400" />
              Campagnes
            </h3>

            {/* Period selector */}
            <div className="flex items-center bg-slate-900/80 rounded-lg p-0.5" data-testid="period-selector">
              {PERIODS.map(p => (
                <button key={p.value}
                  onClick={() => setDays(p.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    days === p.value ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid={`period-${p.value}`}
                >{p.label}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Status filter */}
            <div className="flex items-center bg-slate-900/80 rounded-lg p-0.5" data-testid="status-filter">
              {STATUS_FILTERS.map(f => (
                <button key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    statusFilter === f.value ? "bg-emerald-600 text-white shadow" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid={`filter-${f.value}`}
                >{f.label}</button>
              ))}
            </div>
            <span className="text-xs text-slate-500" data-testid="campaign-count">
              {filtered.length}/{campaigns.length}
            </span>
          </div>
        </div>

        {/* Table */}
        {loadingData ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune campagne {statusFilter === "active" ? "active" : statusFilter === "paused" ? "en pause" : ""}</p>
            {statusFilter !== "all" && (
              <button onClick={() => setStatusFilter("all")} className="text-blue-400 text-xs mt-1 hover:underline">
                Voir toutes les campagnes
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-400 text-xs">Campagne</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">Impr.</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">Clics</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">CTR</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">Cout</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">Conv.</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400 text-xs">Cout/Conv</th>
                  <th className="text-center px-3 py-2.5 font-medium text-slate-400 text-xs">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {filtered.map((c, i) => {
                  const costPerConv = c.conversions > 0 ? c.cost / c.conversions : null;
                  const isActive = c.status === "ENABLED";
                  return (
                    <tr key={c.id || i} className={`transition-colors ${isActive ? "hover:bg-slate-700/20" : "opacity-50 hover:opacity-70"}`}>
                      <td className="px-4 py-2.5 font-medium max-w-xs truncate" title={c.name}>{c.name}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtN(c.impressions)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtN(c.clicks)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{(c.ctr || 0).toFixed(2)}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-400">{fmt(c.cost)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-purple-400">{fmtN(c.conversions)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-emerald-400">{costPerConv ? fmt(costPerConv) : "-"}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          isActive ? "bg-green-500/20 text-green-400" :
                          c.status === "REMOVED" ? "bg-red-500/20 text-red-400" :
                          "bg-slate-500/20 text-slate-400"
                        }`}>
                          {isActive ? "Actif" : c.status === "REMOVED" ? "Supprime" : "Pause"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, alert, highlight }) {
  const colors = {
    blue: 'border-blue-500/30 text-blue-400',
    green: 'border-green-500/30 text-green-400',
    amber: 'border-amber-500/30 text-amber-400',
    purple: 'border-purple-500/30 text-purple-400',
    cyan: 'border-cyan-500/30 text-cyan-400',
    pink: 'border-pink-500/30 text-pink-400',
    emerald: 'border-emerald-500/30 text-emerald-400',
  };

  return (
    <div className={`border rounded-lg p-2.5 ${colors[color]} ${highlight ? "ring-1 ring-emerald-500/50 bg-emerald-500/5" : "bg-slate-800/30"} ${alert ? "ring-1 ring-amber-500/40" : ""}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[11px] text-slate-400">{label}</span>
        {alert && <AlertTriangle className="w-3 h-3 text-amber-400" />}
      </div>
      <p className="text-base font-bold text-white">{value}</p>
    </div>
  );
}
